/**
 * The ONE precedence chain that turns a server error body into the sentence a
 * rep may read. Both `fetchJson` (via `ApiProblemError.userMessage`) and
 * `errorMessageFromResponse` call it, so the two cannot drift.
 *
 * Why this order: `src/http/responder.ts` `problem()` always emits `title` (a
 * short summary) and optionally `detail` (the specific sentence), and
 * `problemWithError` mirrors the legacy `error.message` into `detail`. No
 * backend route emits `message` alongside `title`, and none emits
 * `userMessage` — `userMessage` is the mini-app/BFF field, kept first so the
 * web client and the mini-apps (`mini-apps/_shared/fetch-error.ts`) agree.
 * `message` and `error.message` only occur on legacy, title-less bodies, so
 * they rank after the `problem()` fields. A string `error` is a machine code
 * (`{ error: 'not_found' }` at ~250 routes) and is never shown.
 */

/** A trimmed, non-empty string, or `undefined` for a missing/blank one — a
 *  blank `title`/`detail`/`message` (`{ title: "" }`) must never win the
 *  precedence chain below just because it's present and string-typed, and
 *  must never mask a later, actually-populated candidate. */
export function blankToUndefined(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

const STATUS_PHRASE =
  'Bad Gateway|Service Unavailable|Gateway Timeout|Internal Server Error|Not Found|Bad Request|Forbidden|Unauthorized|Too Many Requests|Conflict';
// Phrases only a proxy or runtime says; "Forbidden" / "Not Found" can be real copy.
const GATEWAY_PHRASE = 'Bad Gateway|Service Unavailable|Gateway Timeout|Internal Server Error';

const RAW_HTTP_DIAGNOSTICS: readonly RegExp[] = [
  // "HTTP 502 Bad Gateway", "HTTP/1.1 500", "Error 500", "Request failed: 503"
  /^(?:HTTP\/?[\d.]*|Error|Request failed:?)\s*[1-5]\d{2}\b/i,
  // "Request failed with status code 404", "HTTP error! status: 404"
  /\bstatus(?: code)?:?\s*[45]\d{2}\b/i,
  // "502", "502: x", "502 Bad Gateway" — but not "250 contacts max per batch"
  new RegExp(`^[45]\\d{2}(?:$|\\s*:|\\s+(?:${STATUS_PHRASE})\\b)`, 'i'),
  // a bare gateway phrase: "Bad Gateway", "Internal Server Error."
  new RegExp(`^(?:${GATEWAY_PHRASE})\\W*$`, 'i'),
];

/** True for a `message`/`error.message` candidate that reads as a raw
 *  upstream HTTP diagnostic (`"HTTP 502 Bad Gateway"`, `"Request failed with
 *  status code 404"`) rather than genuine human copy — CLAUDE.md's rule that
 *  a rep never sees a raw status code. Deliberately scoped to the
 *  `message`/`error.message` fallback candidates only: `title`/`detail` come
 *  from the server's own `problem()` envelope and legitimately contain
 *  digits (e.g. a detail reading "Now at v40"), so this must never gate
 *  those two fields.
 *
 *  Anchored to raw-diagnostic shapes rather than "any 3-digit number or
 *  status phrase anywhere": that matched real copy such as "250 contacts max
 *  per batch" or "Salesforce returned Internal Server Error while saving". */
export function looksLikeRawHttpDiagnostic(value: string): boolean {
  return RAW_HTTP_DIAGNOSTICS.some((re) => re.test(value));
}

/**
 * `body.userMessage` → `body.detail` → `body.title` → `body.message` →
 * `body.error.message` — the documented cross-app rule
 * (`mini-apps/_shared/fetch-error.ts`'s `throwFetchError`, which calls this). A string-typed
 * `body.error` is a machine code and is deliberately never surfaced. Every
 * candidate is blank-trimmed first (a `{ title: "" }` falls through exactly
 * like a missing `title`), and the last two candidates (`message`,
 * `error.message`) are additionally rejected when they look like a raw HTTP
 * diagnostic rather than human copy, falling through to the digit-free
 * fallback instead.
 */
export function extractUserMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const record = body as Record<string, unknown>;
  const userMessage = blankToUndefined(record.userMessage);
  if (userMessage) return userMessage;
  const detail = blankToUndefined(record.detail);
  if (detail) return detail;
  const title = blankToUndefined(record.title);
  if (title) return title;
  const message = blankToUndefined(record.message);
  if (message && !looksLikeRawHttpDiagnostic(message)) return message;
  const nested = record.error;
  if (nested && typeof nested === 'object') {
    const nestedMessage = blankToUndefined((nested as Record<string, unknown>).message);
    if (nestedMessage && !looksLikeRawHttpDiagnostic(nestedMessage)) return nestedMessage;
  }
  return undefined;
}
